export type Language = "ru" | "et" | "en";

export const SUPPORTED_LANGUAGES: readonly Language[] = ["ru", "et", "en"] as const;

export const DEFAULT_LANGUAGE: Language = "ru";

export interface Translations {
  // Controls & Header
  torch: string;
  torch_on: string;
  torch_aria_on: string;
  torch_aria_off: string;
  sound_on: string;
  sound_off: string;
  sound_aria_on: string;
  sound_aria_off: string;
  camera_switch_aria: string;
  camera_title: string;
  camera_default_label: string;
  manual_entry_btn: string;
  manual_entry_aria: string;
  mode_single: string;
  mode_batch: string;
  mode_aria: string;
  recognition_mode_barcode: string;
  recognition_mode_price_tag: string;
  recognition_mode_aria: string;
  price_tag_capture: string;
  price_tag_capture_aria: string;
  price_tag_confirm_title: string;
  price_tag_confirm_hint: string;
  price_tag_retry: string;
  debug_title: string;
  debug_close: string;
  debug_enable: string;
  debug_hint: string;
  debug_storage_failed: string;
  debug_export: string;
  debug_show_json: string;
  debug_clear: string;
  debug_copy_hint: string;
  debug_empty: string;
  debug_frame: string;
  debug_no_text: string;
  debug_synced: string;
  debug_pending: string;
  store_select_placeholder: string;
  store_select_aria: string;
  language_switch_aria: string;

  // Viewfinder
  scanned_badge: string;
  scan_error_badge: string;
  offline_badge: string;
  syncing_badge: string;

  cam_start_hint: string;

  // Low Light Prompt
  low_light_text: string;

  // Connection Banner
  banner_synced: string;
  banner_syncing: string;
  banner_syncing_count: string;
  banner_offline_queue: string;
  banner_offline_local: string;

  // Zoom Controls
  zoom_toggle_expand_aria: string;
  zoom_toggle_collapse_aria: string;
  zoom_reset_1x_aria: string;
  zoom_set_2x_aria: string;
  zoom_slider_aria: string;

  // Local scan history
  history_title: string;
  history_toggle_label: string;
  history_toggle_expand_aria: string;
  history_toggle_collapse_aria: string;
  history_search_btn: string;
  history_search_aria: string;
  history_open_aria: string;

  // Batch Panel / Cart Bottom Sheet
  batch_title: string;
  batch_clear_btn: string;
  batch_clear_aria: string;
  batch_empty_hint: string;
  batch_remove_aria: string;
  batch_remove_title: string;
  batch_submit_btn: string;
  batch_submit_count: string;
  cart_title: string;
  cart_total: string;
  cart_items_count: string;
  cart_increase_qty: string;
  cart_decrease_qty: string;
  cart_expand_aria: string;
  cart_collapse_aria: string;
  cart_empty_hint: string;
  cart_item_unit_price: string;
  cart_item_subtotal: string;

  // Manual Barcode Input
  manual_title: string;
  manual_close_aria: string;
  manual_hint: string;
  manual_placeholder: string;
  manual_clear_aria: string;
  manual_counter: string;
  manual_cancel_btn: string;
  manual_submit_btn: string;

  // Validation Errors
  val_empty: string;
  val_digits_only: string;
  val_invalid_length: string;
  val_checksum_error: string;
  val_checksum_invalid: string;
  val_invalid_format: string;

  // Overlay State Messages
  state_authenticating: string;
  state_camera_ready: string;
  state_scanning: string;
  state_detected: string;
  state_lookup: string;
  state_not_found: string;
  state_scanning_tag: string;
  state_lookup_tag: string;
  state_not_found_tag: string;
  state_offline: string;
  state_session_expired: string;
  overlay_best_price: string;
  overlay_save: string;
  overlay_generic_error: string;
  overlay_favorite_add_aria: string;
  overlay_favorite_remove_aria: string;
  overlay_close_aria: string;
  overlay_back_link: string;
  overlay_missing_body: string;

  // Camera Errors
  cam_perm_title: string;
  cam_perm_msg: string;
  cam_not_found_title: string;
  cam_not_found_msg: string;
  cam_retry_btn: string;
  cam_manual_btn: string;

  // Auth / Startup Errors
  auth_telegram_only: string;
  auth_missing_session: string;
  auth_session_expired: string;
  auth_failed: string;

  // RPC Errors
  rpc_INVALID_BARCODE: string;
  rpc_PRODUCT_NOT_FOUND: string;
  rpc_NO_PRICE_DATA: string;
  rpc_RATE_LIMITED: string;
  rpc_AGENT_UNAVAILABLE: string;
  rpc_TIMEOUT: string;
  rpc_SESSION_EXPIRED: string;
  rpc_UNAUTHORIZED: string;
  rpc_INTERNAL_ERROR: string;
  rpc_INVALID_REQUEST: string;
  rpc_METHOD_NOT_ALLOWED: string;
  rpc_NO_TEXT_DETECTED: string;
  rpc_PRODUCT_NOT_MATCHED: string;
  rpc_FALLBACK: string;
}

export const ruTranslations: Translations = {
  torch: "Фонарик",
  torch_on: "Фонарик вкл",
  torch_aria_on: "Выключить фонарик",
  torch_aria_off: "Включить фонарик",
  sound_on: "Звук",
  sound_off: "Без звука",
  sound_aria_on: "Выключить звук сканирования",
  sound_aria_off: "Включить звук сканирования",
  camera_switch_aria: "Переключить камеру",
  camera_title: "Камера: {label}",
  camera_default_label: "Камера",
  manual_entry_btn: "Ввести код",
  manual_entry_aria: "Ввести штрихкод вручную",
  mode_single: "Одиночный",
  mode_batch: "Пакетный",
  mode_aria: "Режим сканирования",
  recognition_mode_barcode: "Штрихкод",
  recognition_mode_price_tag: "Ценник",
  recognition_mode_aria: "Режим распознавания",
  price_tag_capture: "Сканировать ценник",
  price_tag_capture_aria: "Распознать ценник в рамке",
  price_tag_confirm_title: "Это этот товар?",
  price_tag_confirm_hint: "Выберите вариант — сравнение цен без подтверждения не показываем.",
  price_tag_retry: "Снять ещё раз",
  debug_title: "Отладка OCR",
  debug_close: "Закрыть",
  debug_enable: "Записывать распознавание ценников",
  debug_hint: "При включённой записи текст, поля и ошибки автоматически отправляются в дашборд (Сканер → Отладка OCR), где хранятся 30 дней. Последние 20 попыток также остаются на телефоне; старые записи удаляются при заполнении хранилища. Кадр остаётся только на телефоне до следующего снимка или закрытия приложения и не входит в JSON.",
  debug_storage_failed: "Не удалось сохранить журнал на устройстве. Выгрузите его до закрытия приложения.",
  debug_export: "Выгрузить JSON",
  debug_show_json: "Показать JSON",
  debug_clear: "Очистить на телефоне",
  debug_copy_hint: "Нажмите на текст, чтобы выделить и скопировать журнал:",
  debug_empty: "Включите запись, закройте окно и снимите ценник.",
  debug_frame: "Фрагмент, переданный в OCR",
  debug_no_text: "OCR вернул пустой текст",
  debug_synced: "Сохранено в дашборде",
  debug_pending: "Ожидает отправки в дашборд",
  store_select_placeholder: "В каком вы магазине?",
  store_select_aria: "В каком вы магазине?",
  language_switch_aria: "Выбор языка",

  cam_start_hint: "Нажмите, чтобы включить камеру",

  scanned_badge: "Отсканировано",
  scan_error_badge: "Ошибка сканирования",
  offline_badge: "Офлайн-режим",
  syncing_badge: "Синхронизация…",

  low_light_text: "Слишком темно — включите фонарик",

  banner_synced: "Сеть восстановлена • Синхронизировано",
  banner_syncing: "Синхронизация…",
  banner_syncing_count: "Синхронизация ({count})…",
  banner_offline_queue: "Офлайн • {count} в очереди",
  banner_offline_local: "Офлайн",

  zoom_toggle_expand_aria: "Открыть зум, сейчас {zoom}",
  zoom_toggle_collapse_aria: "Скрыть панель зума",
  zoom_reset_1x_aria: "Сбросить зум на 1x",
  zoom_set_2x_aria: "Установить зум 2x",
  zoom_slider_aria: "Регулировка зума",

  history_title: "Отсканированные за сессию",
  history_toggle_label: "Отсканированные",
  history_toggle_expand_aria: "Показать отсканированные за сессию, {count}",
  history_toggle_collapse_aria: "Скрыть отсканированные",
  history_search_btn: "Поиск",
  history_search_aria: "Найти цены для {barcode}",
  history_open_aria: "Открыть сводку цен: {name}",

  batch_title: "Отсканировано",
  batch_clear_btn: "Очистить",
  batch_clear_aria: "Очистить список",
  batch_empty_hint: "Наведите камеру на штрихкоды для добавления в список",
  batch_remove_aria: "Удалить {barcode}",
  batch_remove_title: "Удалить позицию",
  batch_submit_btn: "Готово",
  batch_submit_count: "Готово ({count})",
  cart_title: "Корзина",
  cart_total: "Итого",
  cart_items_count: "{count} шт.",
  cart_increase_qty: "Увеличить количество",
  cart_decrease_qty: "Уменьшить количество",
  cart_expand_aria: "Развернуть корзину",
  cart_collapse_aria: "Свернуть корзину",
  cart_empty_hint: "Наведите камеру на штрихкоды для добавления в корзину",
  cart_item_unit_price: "{price} € / шт.",
  cart_item_subtotal: "{qty} × {price} € = {total} €",

  manual_title: "Ввести штрихкод",
  manual_close_aria: "Закрыть",
  manual_hint: "Введите 8, 12 или 13 цифр штрихкода (EAN-8, UPC-A, EAN-13)",
  manual_placeholder: "Например: 4740012345678",
  manual_clear_aria: "Очистить поле ввода",
  manual_counter: "Цифр: ",
  manual_cancel_btn: "Отмена",
  manual_submit_btn: "Добавить",

  val_empty: "Введите штрихкод",
  val_digits_only: "Штрихкод должен содержать только цифры",
  val_invalid_length: "Штрихкод должен содержать 8, 12 или 13 цифр",
  val_checksum_error: "Неверная контрольная сумма (ожидается {expected})",
  val_checksum_invalid: "Неверная контрольная сумма EAN",
  val_invalid_format: "Неверный формат штрихкода",

  state_authenticating: "Проверка сессии сканирования…",
  state_camera_ready: "Запуск камеры…",
  state_scanning: "Наведите камеру на штрихкод",
  state_detected: "Штрихкод обнаружен, чтение…",
  state_lookup: "Поиск цен…",
  state_not_found: "Товар не найден",
  state_scanning_tag: "Наведите рамку на один ценник",
  state_lookup_tag: "Читаем ценник…",
  state_not_found_tag: "Не удалось сопоставить ценник. Подойдите ближе.",
  state_offline: "Переподключение к серверу…",
  state_session_expired: "Сессия сканирования истекла. Откройте сканер заново из бота.",
  overlay_best_price: "Лучшая цена: {price}",
  overlay_save: "Экономия: {saving}",
  overlay_generic_error: "Что-то пошло не так",
  overlay_favorite_add_aria: "Добавить в мои товары",
  overlay_favorite_remove_aria: "Убрать из моих товаров",
  overlay_close_aria: "Закрыть карточку товара",
  overlay_back_link: "Назад к отсканированным",
  overlay_missing_body: "Товар пока отсутствует в нашей базе",

  cam_perm_title: "Нет доступа к камере",
  cam_perm_msg: "Для сканирования штрихкодов разрешите доступ к камере в настройках Telegram или браузера.",
  cam_not_found_title: "Камера не найдена",
  cam_not_found_msg: "На вашем устройстве нет камеры или она недоступна. Вы можете ввести штрихкод вручную.",
  cam_retry_btn: "Повторить попытку",
  cam_manual_btn: "Ввести штрихкод вручную",

  auth_telegram_only: "Этот сканер можно открыть только в Telegram.",
  auth_missing_session: "Отсутствует сессия сканирования. Откройте сканер заново из бота.",
  auth_session_expired: "Сессия сканирования истекла. Откройте сканер заново из бота.",
  auth_failed: "Ошибка авторизации. Откройте сканер заново из бота.",

  rpc_INVALID_BARCODE: "Неверный штрихкод",
  rpc_PRODUCT_NOT_FOUND: "Товар не найден",
  rpc_NO_PRICE_DATA: "Нет данных о ценах",
  rpc_RATE_LIMITED: "Слишком много запросов",
  rpc_AGENT_UNAVAILABLE: "Сервис временно недоступен",
  rpc_TIMEOUT: "Превышено время ожидания",
  rpc_SESSION_EXPIRED: "Сессия сканирования истекла",
  rpc_UNAUTHORIZED: "Ошибка авторизации",
  rpc_INTERNAL_ERROR: "Ошибка сервера",
  rpc_INVALID_REQUEST: "Некорректный запрос",
  rpc_METHOD_NOT_ALLOWED: "Метод не поддерживается",
  rpc_NO_TEXT_DETECTED: "Не удалось прочитать ценник. Подойдите ближе.",
  rpc_PRODUCT_NOT_MATCHED: "Товар по ценнику не найден",
  rpc_FALLBACK: "Ошибка запроса",
};

export const etTranslations: Translations = {
  torch: "Taskulamp",
  torch_on: "Taskulamp sees",
  torch_aria_on: "Lülita taskulamp välja",
  torch_aria_off: "Lülita taskulamp sisse",
  sound_on: "Heli",
  sound_off: "Hääletu",
  sound_aria_on: "Lülita skannimisheli välja",
  sound_aria_off: "Lülita skannimisheli sisse",
  camera_switch_aria: "Vaheta kaamerat",
  camera_title: "Kaamera: {label}",
  camera_default_label: "Kaamera",
  manual_entry_btn: "Sisesta kood",
  manual_entry_aria: "Sisesta vöötkood käsitsi",
  mode_single: "Üksik",
  mode_batch: "Partii",
  mode_aria: "Skannimisrežiim",
  recognition_mode_barcode: "Vöötkood",
  recognition_mode_price_tag: "Hinnasilt",
  recognition_mode_aria: "Tuvastusrežiim",
  price_tag_capture: "Skanni hinnasilt",
  price_tag_capture_aria: "Tuvasta hinnasilt raamis",
  price_tag_confirm_title: "Kas see on õige toode?",
  price_tag_confirm_hint: "Vali variant — madala kindlusega hinda automaatselt ei näita.",
  price_tag_retry: "Proovi uuesti",
  debug_title: "OCR-i silumine",
  debug_close: "Sulge",
  debug_enable: "Salvesta hinnasiltide tuvastamise logi",
  debug_hint: "Salvestamise ajal saadetakse tekst, väljad ja vead automaatselt töölauale (Skanner → OCR-i silumine), kus neid hoitakse 30 päeva. Viimased 20 katset jäävad ka telefoni; ruumi lõppedes eemaldatakse vanemad kirjed. Kaader jääb ainult telefoni järgmise pildi või rakenduse sulgemiseni ega kuulu JSON-i.",
  debug_storage_failed: "Logi ei õnnestunud seadmesse salvestada. Ekspordi see enne rakenduse sulgemist.",
  debug_export: "Ekspordi JSON",
  debug_show_json: "Näita JSON-i",
  debug_clear: "Tühjenda telefonis",
  debug_copy_hint: "Puuduta teksti, et logi valida ja kopeerida:",
  debug_empty: "Lülita salvestamine sisse, sulge aken ja pildista hinnasilti.",
  debug_frame: "OCR-ile saadetud pildiosa",
  debug_no_text: "OCR tagastas tühja teksti",
  debug_synced: "Salvestatud töölauale",
  debug_pending: "Ootab saatmist töölauale",
  store_select_placeholder: "Mis poes sa oled?",
  store_select_aria: "Mis poes sa oled?",
  language_switch_aria: "Keele valik",

  cam_start_hint: "Puuduta, et kaamera sisse lülitada",

  scanned_badge: "Skannitud",
  scan_error_badge: "Skannimisviga",
  offline_badge: "Võrguühenduseta",
  syncing_badge: "Sünkroonimine…",

  low_light_text: "Liiga pime — lülita taskulamp sisse",

  banner_synced: "Ühendus taastatud • Sünkroonitud",
  banner_syncing: "Sünkroonimine…",
  banner_syncing_count: "Sünkroonimine ({count})…",
  banner_offline_queue: "Võrguühenduseta • {count} järjekorras",
  banner_offline_local: "Võrguühenduseta",

  zoom_toggle_expand_aria: "Ava suum, praegu {zoom}",
  zoom_toggle_collapse_aria: "Peida suumi riba",
  zoom_reset_1x_aria: "Lähtesta suum 1x peale",
  zoom_set_2x_aria: "Määra suum 2x peale",
  zoom_slider_aria: "Suumi reguleerimine",

  history_title: "Selle seansi skannid",
  history_toggle_label: "Skannitud",
  history_toggle_expand_aria: "Näita selle seansi skanne, {count}",
  history_toggle_collapse_aria: "Peida skannid",
  history_search_btn: "Otsi",
  history_search_aria: "Otsi hindu koodile {barcode}",
  history_open_aria: "Ava hinnakokkuvõte: {name}",

  batch_title: "Skannitud",
  batch_clear_btn: "Tühjenda",
  batch_clear_aria: "Tühjenda nimekiri",
  batch_empty_hint: "Suuna kaamera vöötkoodidele nimekirja lisamiseks",
  batch_remove_aria: "Kustuta {barcode}",
  batch_remove_title: "Kustuta kirje",
  batch_submit_btn: "Valmis",
  batch_submit_count: "Valmis ({count})",
  cart_title: "Ostukorv",
  cart_total: "Kokku",
  cart_items_count: "{count} tk",
  cart_increase_qty: "Suurenda kogust",
  cart_decrease_qty: "Vähenda kogust",
  cart_expand_aria: "Ava ostukorv",
  cart_collapse_aria: "Sulge ostukorv",
  cart_empty_hint: "Suuna kaamera vöötkoodidele ostukorvi lisamiseks",
  cart_item_unit_price: "{price} € / tk",
  cart_item_subtotal: "{qty} × {price} € = {total} €",

  manual_title: "Sisesta vöötkood",
  manual_close_aria: "Sulge",
  manual_hint: "Sisesta 8, 12 või 13 vöötkoodi numbrit (EAN-8, UPC-A, EAN-13)",
  manual_placeholder: "Näiteks: 4740012345678",
  manual_clear_aria: "Tühjenda sisestusväli",
  manual_counter: "Numbreid: ",
  manual_cancel_btn: "Loobu",
  manual_submit_btn: "Lisa",

  val_empty: "Sisesta vöötkood",
  val_digits_only: "Vöötkood peab sisaldama ainult numbreid",
  val_invalid_length: "Vöötkood peab sisaldama 8, 12 või 13 numbrit",
  val_checksum_error: "Vigane kontrollkood (oodatud {expected})",
  val_checksum_invalid: "Vigane EAN kontrollkood",
  val_invalid_format: "Vigane vöötkoodi vorming",

  state_authenticating: "Skannimisseansi kontrollimine…",
  state_camera_ready: "Kaamera käivitamine…",
  state_scanning: "Suuna kaamera vöötkoodile",
  state_detected: "Vöötkood leitud, loen…",
  state_lookup: "Hindade otsimine…",
  state_not_found: "Toodet ei leitud",
  state_scanning_tag: "Suuna raam ühe hinnasildi peale",
  state_lookup_tag: "Loen hinnasilti…",
  state_not_found_tag: "Hinnasilti ei õnnestunud siduda. Mine lähemale.",
  state_offline: "Taasühendamine serveriga…",
  state_session_expired: "Skannimisseanss on aegunud. Ava skanner uuesti botist.",
  overlay_best_price: "Parim hind: {price}",
  overlay_save: "Sääst: {saving}",
  overlay_generic_error: "Midagi läks valesti",
  overlay_favorite_add_aria: "Lisa minu toodete hulka",
  overlay_favorite_remove_aria: "Eemalda minu toodetest",
  overlay_close_aria: "Sulge tootekaart",
  overlay_back_link: "Tagasi skannide juurde",
  overlay_missing_body: "Toodet meie andmebaasis veel ei ole",

  cam_perm_title: "Kaamerale puudub ligipääs",
  cam_perm_msg: "Vöötkoodide skannimiseks luba kaamerale juurdepääs Telegrami või brauseri seadetes.",
  cam_not_found_title: "Kaamerat ei leitud",
  cam_not_found_msg: "Sinu seadmes ei leitud kaamerat või see pole saadaval. Saad vöötkoodi käsitsi sisestada.",
  cam_retry_btn: "Proovi uuesti",
  cam_manual_btn: "Sisesta vöötkood käsitsi",

  auth_telegram_only: "Seda skannerit saab avada ainult Telegramis.",
  auth_missing_session: "Puudub skannimisseanss. Ava skanner uuesti botist.",
  auth_session_expired: "Skannimisseanss on aegunud. Ava skanner uuesti botist.",
  auth_failed: "Autentimistõrge. Ava skanner uuesti botist.",

  rpc_INVALID_BARCODE: "Vigane vöötkood",
  rpc_PRODUCT_NOT_FOUND: "Toodet ei leitud",
  rpc_NO_PRICE_DATA: "Hinnainfo puudub",
  rpc_RATE_LIMITED: "Liiga palju päringuid",
  rpc_AGENT_UNAVAILABLE: "Teenus pole ajutiselt saadaval",
  rpc_TIMEOUT: "Päring aegus",
  rpc_SESSION_EXPIRED: "Skannimisseanss on aegunud",
  rpc_UNAUTHORIZED: "Autentimistõrge",
  rpc_INTERNAL_ERROR: "Serveri viga",
  rpc_INVALID_REQUEST: "Vigane päring",
  rpc_METHOD_NOT_ALLOWED: "Meetodit ei toetata",
  rpc_NO_TEXT_DETECTED: "Hinnasilti ei õnnestunud lugeda. Mine lähemale.",
  rpc_PRODUCT_NOT_MATCHED: "Hinnasildi järgi toodet ei leitud",
  rpc_FALLBACK: "Päringu viga",
};

export const enTranslations: Translations = {
  torch: "Torch",
  torch_on: "Torch On",
  torch_aria_on: "Turn off flashlight",
  torch_aria_off: "Turn on flashlight",
  sound_on: "Sound",
  sound_off: "Muted",
  sound_aria_on: "Mute scan sound",
  sound_aria_off: "Enable scan sound",
  camera_switch_aria: "Switch camera",
  camera_title: "Camera: {label}",
  camera_default_label: "Camera",
  manual_entry_btn: "Enter code",
  manual_entry_aria: "Enter barcode manually",
  mode_single: "Single",
  mode_batch: "Batch",
  mode_aria: "Scan mode",
  recognition_mode_barcode: "Barcode",
  recognition_mode_price_tag: "Price tag",
  recognition_mode_aria: "Recognition mode",
  price_tag_capture: "Scan price tag",
  price_tag_capture_aria: "Recognize the price tag in the frame",
  price_tag_confirm_title: "Is this the product?",
  price_tag_confirm_hint: "Pick a match — we will not show a comparison until you confirm.",
  price_tag_retry: "Try again",
  debug_title: "OCR debug",
  debug_close: "Close",
  debug_enable: "Record price tag recognition",
  debug_hint: "While recording is enabled, text, fields and errors upload automatically to Dashboard → Scanner → OCR debug and are retained for 30 days. The last 20 attempts also stay on the phone; older entries are removed when storage fills up. The frame stays only on the phone until the next capture or app close and is not included in JSON.",
  debug_storage_failed: "Could not save the log on this device. Export it before closing the app.",
  debug_export: "Export JSON",
  debug_show_json: "Show JSON",
  debug_clear: "Clear on phone",
  debug_copy_hint: "Tap the text to select and copy the log:",
  debug_empty: "Enable recording, close this window and capture a price tag.",
  debug_frame: "Crop sent to OCR",
  debug_no_text: "OCR returned empty text",
  debug_synced: "Saved to dashboard",
  debug_pending: "Waiting to upload to dashboard",
  store_select_placeholder: "Which store are you in?",
  store_select_aria: "Which store are you in?",
  language_switch_aria: "Language selection",

  cam_start_hint: "Tap to turn on the camera",

  scanned_badge: "Scanned",
  scan_error_badge: "Scan error",
  offline_badge: "Offline mode",
  syncing_badge: "Syncing…",

  low_light_text: "Too dark — turn on flashlight",

  banner_synced: "Connection restored • Synced",
  banner_syncing: "Syncing…",
  banner_syncing_count: "Syncing ({count})…",
  banner_offline_queue: "Offline • {count} in queue",
  banner_offline_local: "Offline",

  zoom_toggle_expand_aria: "Open zoom, currently {zoom}",
  zoom_toggle_collapse_aria: "Hide zoom controls",
  zoom_reset_1x_aria: "Reset zoom to 1x",
  zoom_set_2x_aria: "Set zoom to 2x",
  zoom_slider_aria: "Zoom adjustment",

  history_title: "Scanned this session",
  history_toggle_label: "Scanned",
  history_toggle_expand_aria: "Show scans from this session, {count}",
  history_toggle_collapse_aria: "Hide scanned items",
  history_search_btn: "Search",
  history_search_aria: "Search prices for {barcode}",
  history_open_aria: "Open price summary: {name}",

  batch_title: "Scanned",
  batch_clear_btn: "Clear",
  batch_clear_aria: "Clear list",
  batch_empty_hint: "Point the camera at barcodes to add them to the list",
  batch_remove_aria: "Remove {barcode}",
  batch_remove_title: "Remove item",
  batch_submit_btn: "Done",
  batch_submit_count: "Done ({count})",
  cart_title: "Cart",
  cart_total: "Total",
  cart_items_count: "{count} pcs",
  cart_increase_qty: "Increase quantity",
  cart_decrease_qty: "Decrease quantity",
  cart_expand_aria: "Expand cart",
  cart_collapse_aria: "Collapse cart",
  cart_empty_hint: "Point the camera at barcodes to add them to the cart",
  cart_item_unit_price: "€{price} / item",
  cart_item_subtotal: "{qty} × €{price} = €{total}",

  manual_title: "Enter barcode",
  manual_close_aria: "Close",
  manual_hint: "Enter 8, 12 or 13 barcode digits (EAN-8, UPC-A, EAN-13)",
  manual_placeholder: "e.g. 4740012345678",
  manual_clear_aria: "Clear input field",
  manual_counter: "Digits: ",
  manual_cancel_btn: "Cancel",
  manual_submit_btn: "Add",

  val_empty: "Enter a barcode",
  val_digits_only: "Barcode must contain only digits",
  val_invalid_length: "Barcode must contain 8, 12 or 13 digits",
  val_checksum_error: "Invalid checksum (expected {expected})",
  val_checksum_invalid: "Invalid EAN checksum",
  val_invalid_format: "Invalid barcode format",

  state_authenticating: "Checking your scan session…",
  state_camera_ready: "Starting camera…",
  state_scanning: "Point the camera at a barcode",
  state_detected: "Barcode detected, reading…",
  state_lookup: "Looking up prices…",
  state_not_found: "Product not found",
  state_scanning_tag: "Point the frame at one price tag",
  state_lookup_tag: "Reading the price tag…",
  state_not_found_tag: "Could not match the price tag. Move closer.",
  state_offline: "Reconnecting to server…",
  state_session_expired: "Scan session has expired. Open the scanner again from the bot.",
  overlay_best_price: "Best price: {price}",
  overlay_save: "Save: {saving}",
  overlay_generic_error: "Something went wrong",
  overlay_favorite_add_aria: "Add to my products",
  overlay_favorite_remove_aria: "Remove from my products",
  overlay_close_aria: "Close product card",
  overlay_back_link: "Back to scanned",
  overlay_missing_body: "This product is not in our catalog yet",

  cam_perm_title: "No camera access",
  cam_perm_msg: "To scan barcodes, allow camera access in Telegram or browser settings.",
  cam_not_found_title: "Camera not found",
  cam_not_found_msg: "No camera found on your device or it is unavailable. You can enter the barcode manually.",
  cam_retry_btn: "Try again",
  cam_manual_btn: "Enter barcode manually",

  auth_telegram_only: "This scanner can only be opened inside Telegram.",
  auth_missing_session: "Missing scan session. Open the scanner again from the bot.",
  auth_session_expired: "Scan session has expired. Open the scanner again from the bot.",
  auth_failed: "Authentication failed. Open the scanner again from the bot.",

  rpc_INVALID_BARCODE: "Invalid barcode",
  rpc_PRODUCT_NOT_FOUND: "Product not found",
  rpc_NO_PRICE_DATA: "No price data",
  rpc_RATE_LIMITED: "Too many requests",
  rpc_AGENT_UNAVAILABLE: "Service temporarily unavailable",
  rpc_TIMEOUT: "Request timed out",
  rpc_SESSION_EXPIRED: "Scan session has expired",
  rpc_UNAUTHORIZED: "Authorization error",
  rpc_INTERNAL_ERROR: "Server error",
  rpc_INVALID_REQUEST: "Invalid request",
  rpc_METHOD_NOT_ALLOWED: "Method not supported",
  rpc_NO_TEXT_DETECTED: "Could not read the price tag. Move closer.",
  rpc_PRODUCT_NOT_MATCHED: "No catalog match for this price tag",
  rpc_FALLBACK: "Request error",
};

export const TRANSLATIONS: Record<Language, Translations> = {
  ru: ruTranslations,
  et: etTranslations,
  en: enTranslations,
};

/**
 * Formats a localized string by key and interpolates variables ({param}).
 */
export function t(
  lang: Language = DEFAULT_LANGUAGE,
  key: keyof Translations,
  params?: Record<string, string | number>,
): string {
  const dict = TRANSLATIONS[lang] || TRANSLATIONS[DEFAULT_LANGUAGE];
  let text = dict[key] || TRANSLATIONS[DEFAULT_LANGUAGE][key] || String(key);
  if (params) {
    for (const [pKey, pVal] of Object.entries(params)) {
      text = text.replace(new RegExp(`\\{${pKey}\\}`, "g"), String(pVal));
    }
  }
  return text;
}

/**
 * Resolves the effective language from user preference, Telegram initData language_code, or browser locale.
 */
export function resolveLanguage(options?: {
  saved?: string | null;
  telegram?: string | null;
  browser?: string | null;
  fallback?: Language;
}): Language {
  const fallback = options?.fallback || DEFAULT_LANGUAGE;

  if (options?.saved) {
    const s = options.saved.toLowerCase().trim();
    if (s === "ru" || s === "et" || s === "en") {
      return s;
    }
  }

  if (options?.telegram) {
    const tg = options.telegram.toLowerCase().trim();
    if (tg.startsWith("et") || tg.startsWith("est")) return "et";
    if (tg.startsWith("en") || tg.startsWith("eng")) return "en";
    if (tg.startsWith("ru") || tg.startsWith("rus")) return "ru";
  }

  if (options?.browser) {
    const b = options.browser.toLowerCase().trim();
    if (b.startsWith("et") || b.startsWith("est")) return "et";
    if (b.startsWith("en") || b.startsWith("eng")) return "en";
    if (b.startsWith("ru") || b.startsWith("rus")) return "ru";
  }

  return fallback;
}

/**
 * Returns a user-friendly localized error message for standard RPC error codes.
 */
export function getFriendlyRpcErrorMessage(
  code: string,
  langOrFallback?: Language | string,
  fallbackMessage?: string,
): string {
  let lang: Language = DEFAULT_LANGUAGE;
  let fallback = fallbackMessage;

  if (typeof langOrFallback === "string") {
    if (langOrFallback === "ru" || langOrFallback === "et" || langOrFallback === "en") {
      lang = langOrFallback;
    } else if (!fallback) {
      fallback = langOrFallback;
    }
  }

  const rpcKey = `rpc_${code}` as keyof Translations;
  const dict = TRANSLATIONS[lang] || TRANSLATIONS[DEFAULT_LANGUAGE];
  if (rpcKey in dict) {
    return dict[rpcKey];
  }

  return fallback || dict.rpc_FALLBACK;
}
